import WaveSurfer from 'wavesurfer.js';

interface AudioLevel {
  playerNumber: number;
  rms: number;
  wavesurfer: WaveSurfer;
}

export interface LevelMatchResult {
  playerNumber: number;
  gainReduction: number;
}

export const calculateRMS = async (wavesurfer: WaveSurfer): Promise<number> => {
  const audioData = wavesurfer.getDecodedData();
  if (!audioData) return 0;

  let sumSquares = 0;
  const channelData = audioData.getChannelData(0);
  for (let i = 0; i < channelData.length; i++) {
    sumSquares += channelData[i] * channelData[i];
  }
  return Math.sqrt(sumSquares / channelData.length);
};

export const matchAudioLevels = async (players: Map<number, WaveSurfer>): Promise<LevelMatchResult[]> => {
  console.log(`[Level Matching] Starting level matching for ${players.size} players`);
  
  // Skip level matching if there's only one track
  if (players.size <= 1) {
    console.log('[Level Matching] Single track detected, skipping level matching');
    return Array.from(players.keys()).map(playerNumber => ({
      playerNumber,
      gainReduction: 1 // No gain reduction for single track
    }));
  }

  // Wait a moment to ensure all players are fully loaded
  await new Promise(resolve => setTimeout(resolve, 500));

  // Calculate RMS for each player
  const levels: AudioLevel[] = await Promise.all(
    Array.from(players.entries()).map(async ([playerNumber, wavesurfer]) => {
      // Ensure the audio is loaded
      if (!wavesurfer.getDuration()) {
        await new Promise<void>((resolve) => {
          const readyHandler = () => {
            wavesurfer.un('ready', readyHandler);
            resolve();
          };
          wavesurfer.on('ready', readyHandler);
        });
      }
      
      const rms = await calculateRMS(wavesurfer);
      return {
        playerNumber,
        rms,
        wavesurfer,
      };
    })
  );

  // Log RMS values for debugging
  console.log('[Level Matching] RMS values calculated:');
  levels.forEach(level => {
    console.log(`  Player ${level.playerNumber}: RMS = ${level.rms.toFixed(6)}`);
  });

  // Find the lowest RMS value (excluding zero values)
  const validLevels = levels.filter(level => level.rms > 0);
  if (validLevels.length === 0) {
    console.log('[Level Matching] No valid RMS levels found');
    return [];
  }

  const lowestRMS = Math.min(...validLevels.map(level => level.rms));
  console.log(`[Level Matching] Lowest RMS: ${lowestRMS.toFixed(6)}`);
  
  // Calculate gain adjustments
  const results = validLevels.map(level => {
    if (level.rms > 0) {
      const adjustmentFactor = lowestRMS / level.rms;
      // Ensure we don't adjust volumes too drastically
      const gainReduction = Math.min(1, Math.max(0.1, adjustmentFactor));
      const gainReductionDb = 20 * Math.log10(gainReduction);
      console.log(`[Level Matching] Player ${level.playerNumber}: adjustment factor = ${adjustmentFactor.toFixed(6)}, gain reduction = ${gainReduction.toFixed(6)} (${gainReductionDb.toFixed(1)}dB)`);
      return {
        playerNumber: level.playerNumber,
        gainReduction
      };
    }
    return {
      playerNumber: level.playerNumber,
      gainReduction: 1
    };
  });
  
  console.log('[Level Matching] Final results:', results);
  return results;
}; 